import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import './VideoPlayer.css';

const VideoPlayer = forwardRef(({ video, courseId, onVideoEnd, onProgress }, ref) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showSubtitles, setShowSubtitles] = useState(false);
  const controlsTimeoutRef = useRef(null);

  useImperativeHandle(ref, () => videoRef.current);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const handleTimeUpdate = () => {
      const current = videoElement.currentTime;
      const total = videoElement.duration;
      setCurrentTime(current);
      
      // Salvar posição a cada 5 segundos
      if (Math.floor(current) % 5 === 0 && courseId && video?.key) {
        localStorage.setItem(`video_time_${courseId}_${video.key}`, current.toString());
      }
      
      // Reportar progresso
      if (total > 0 && onProgress) {
        const progress = (current / total) * 100;
        onProgress(progress);
      }
    };
    
    const handleLoadedMetadata = () => setDuration(videoElement.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      if (onProgress) onProgress(100);
      if (onVideoEnd) onVideoEnd();
    };

    videoElement.addEventListener('timeupdate', handleTimeUpdate);
    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
    videoElement.addEventListener('ended', handleEnded);

    return () => {
      videoElement.removeEventListener('timeupdate', handleTimeUpdate);
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.removeEventListener('ended', handleEnded);
    };
  }, [onVideoEnd, onProgress, courseId, video?.key]);

  useEffect(() => {
    if (videoRef.current && video?.url) {
      const videoElement = videoRef.current;
      videoElement.load();
      setCurrentTime(0);
      
      // Aguardar o load completar antes de tentar reproduzir
      const handleCanPlay = () => {
        // Restaurar posição salva
        const savedTime = localStorage.getItem(`video_time_${courseId}_${video.key}`);
        if (savedTime) {
          videoElement.currentTime = parseFloat(savedTime);
        }
        
        videoElement.play().then(() => {
          setIsPlaying(true);
        }).catch(err => {
          console.log('Autoplay bloqueado:', err);
          setIsPlaying(false);
        });
        videoElement.removeEventListener('canplay', handleCanPlay);
      };
      
      videoElement.addEventListener('canplay', handleCanPlay);
      
      return () => {
        videoElement.removeEventListener('canplay', handleCanPlay);
      };
    }
  }, [video?.url, courseId, video?.key]);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSeek = (e) => {
    const newTime = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setVolume(newVolume);
    }
  };

  const handleSpeedChange = (speed) => {
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
      setPlaybackRate(speed);
      setShowSpeedMenu(false);
    }
  };

  const toggleSubtitles = () => {
    setShowSubtitles(!showSubtitles);
    if (videoRef.current) {
      const tracks = videoRef.current.textTracks;
      if (tracks.length > 0) {
        tracks[0].mode = !showSubtitles ? 'showing' : 'hidden';
      }
    }
  };

  const toggleFullscreen = () => {
    const container = videoRef.current?.parentElement;
    if (!document.fullscreenElement) {
      container?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  if (!video) {
    return (
      <div className="video-player-placeholder">
        <div className="placeholder-content">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="12" cy="12" r="10" strokeWidth="2"/>
            <polygon points="10 8 16 12 10 16 10 8" fill="currentColor"/>
          </svg>
          <p>Selecione uma aula para começar</p>
        </div>
      </div>
    );
  }

  return (
    <div className="video-player-container" onMouseMove={handleMouseMove}>
      <video
        ref={videoRef}
        className="video-player"
        onClick={togglePlay}
      >
        <source src={video.url} type="video/mp4" />
        Seu navegador não suporta vídeo HTML5.
      </video>

      <div className={`video-controls ${showControls ? 'visible' : ''}`}>
        <div className="progress-bar">
          <input
            type="range"
            min="0"
            max={duration || 0}
            value={currentTime}
            onChange={handleSeek}
            className="progress-slider"
          />
        </div>

        <div className="controls-bottom">
          <button onClick={togglePlay} className="control-btn">
            {isPlaying ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16"/>
                <rect x="14" y="4" width="4" height="16"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <polygon points="5 3 19 12 5 21 5 3"/>
              </svg>
            )}
          </button>

          <div className="time-display">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>

          <div className="volume-control">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
            </svg>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={handleVolumeChange}
              className="volume-slider"
            />
          </div>

          <div className="speed-control">
            <button onClick={() => setShowSpeedMenu(!showSpeedMenu)} className="control-btn">
              {playbackRate}x
            </button>
            {showSpeedMenu && (
              <div className="speed-menu">
                {[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 3, 4].map(speed => (
                  <button
                    key={speed}
                    onClick={() => handleSpeedChange(speed)}
                    className={playbackRate === speed ? 'active' : ''}
                  >
                    {speed}x
                  </button>
                ))}
              </div>
            )}
          </div>

          <button onClick={toggleSubtitles} className={`control-btn ${showSubtitles ? 'active' : ''}`}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM4 12h4v2H4v-2zm10 6H4v-2h10v2zm6 0h-4v-2h4v2zm0-4H10v-2h10v2z"/>
            </svg>
          </button>

          <button onClick={toggleFullscreen} className="control-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
});

export default VideoPlayer;
